import Seo from 'components/seo';
import ContentPage from 'layouts/ContentPage';
import SiteLayout from 'layouts/SiteLayout';
import React from 'react';

const AccountDeletion = () => {
  return (
    <SiteLayout>
        <Seo
          title="Delete Your Account | Furbuz"
          description="How to delete your Furbuz account, what data is removed, retention during the grace period, and how subscriptions are handled."
        />

        <ContentPage
          title="Delete Your Account"
          description="Last Updated: July 17, 2026"
          eyebrow="Account"
        >
            <p>
              This page explains how to delete your Furbuz account and what
              happens to your data afterward. Google Play and App Store users
              can use these steps to request account deletion.
            </p>

            <h2>1. How to delete your account in the app</h2>
            <ol>
              <li>Open the Furbuz app and sign in.</li>
              <li>Go to <strong>Settings</strong>.</li>
              <li>Choose <strong>Delete Account</strong>.</li>
              <li>Read the warning about data removal and confirm.</li>
              <li>
                After you confirm, Furbuz schedules deletion and signs you out.
              </li>
            </ol>
            <p>
              You can also find a short summary in our{' '}
              <a href="/faq">FAQ</a>. For privacy details, see the{' '}
              <a href="/privacy-policy">Privacy Policy</a>.
            </p>

            <h2>2. Grace period and cancellation</h2>
            <p>
              Account deletion uses a grace period so you can reverse an
              accidental request:
            </p>
            <ul>
              <li>
                <strong>Production:</strong> deletion is scheduled for about{' '}
                <strong>30 days</strong> after your request.
              </li>
              <li>
                During that period, the account is marked for deletion and
                cannot be used normally until you cancel the request or the
                period ends.
              </li>
              <li>
                To cancel, sign in again and choose{' '}
                <strong>Cancel Deletion</strong> when prompted. Your account
                then remains active.
              </li>
            </ul>

            <h2>3. What data is deleted</h2>
            <p>
              When deletion completes after the grace period, we remove or
              de-identify account-associated personal data from Furbuz systems,
              including:
            </p>
            <ul>
              <li>Account profile and authentication identifiers used by Furbuz</li>
              <li>Pet profiles and care records you added</li>
              <li>
                Connect content tied to your account (such as posts, comments,
                and messages), subject to safety and legal holds described below
              </li>
              <li>Device and notification registration data for your account</li>
              <li>In-app subscription status records maintained by Furbuz</li>
            </ul>

            <h2>4. Data retention</h2>
            <ul>
              <li>
                Until the grace period ends, your data remains stored so we can
                restore the account if you cancel deletion.
              </li>
              <li>
                After deletion completes, we retain only limited information
                when needed for security, fraud prevention, legal compliance,
                dispute resolution, or financial/audit obligations (for
                example, subscription transaction identifiers already processed
                by Apple or Google).
              </li>
              <li>
                Backups may take additional time to expire after primary
                deletion.
              </li>
            </ul>

            <h2>5. Subscriptions (Furbuz Pro)</h2>
            <p>
              Deleting your Furbuz account <strong>does not</strong>{' '}
              automatically cancel a Furbuz Pro subscription. Billing is managed
              by Apple or Google. Cancel through:
            </p>
            <ul>
              <li>
                <strong>Apple:</strong> Settings → Apple ID → Subscriptions
              </li>
              <li>
                <strong>Google:</strong> Google Play → Payments &amp;
                subscriptions → Subscriptions
              </li>
            </ul>
            <p>
              You can also use Manage Subscription in Furbuz Settings, which
              opens the store subscription page.
            </p>

            <h2>6. Contact</h2>
            <p>
              If you cannot access the app or need help with deletion, email{' '}
              <a href="mailto:support@furbuz.com">support@furbuz.com</a> or{' '}
              <a href="mailto:privacy@furbuz.com">privacy@furbuz.com</a> from
              the address on your Furbuz account and include the email you used
              to sign in.
            </p>
        </ContentPage>
    </SiteLayout>
  );
};

export default AccountDeletion;
